import viewModelFactory from "flow/factories/viewModelFactory";
import viewCache from "flow/utils/viewCache";
import config from "flow/config";
import environment from "flow/configuration/environment";
import moduleLoader from "flow/wrappers/moduleLoader";
import MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";
import { WorkspaceModel } from "flow/entities/WorkspaceModel";
import { SelectedProduct } from "flow/entities/SelectedProduct";
import { WebModuleManifest } from "flow/entities/WebModuleManifest";
import { ProductConfigManifest } from "flow/entities/ProductConfigManifest";
import { HomeConfig } from "flow/entities/HomeConfig";

function getWorkspace(myFlow: MyFlow) {
    return myFlow.getJSON<WorkspaceModel>(config.partialUrls.getWorkspace);
}

function getWebModule(myFlow: MyFlow, baseUrl: string) {
    return myFlow.getJSONWithAnotherBaseUrl<WebModuleManifest>(baseUrl, config.partialUrls.manifest, {
        uiCulture: environment.getUiCulture(),
        ecma: 6, // Not used in Flow 2 but needed to differentiate in ManifestController
        productId: environment.get(environment.keys.productId)
    });
}

function loadBundle(modulePath: string) {
    return moduleLoader.requireSingleAsync(modulePath) as any as JQueryPromise<any>; // Ugly workaround to avoid wrapping in jQueryPromise
}

function getView(myFlow: MyFlow, url: string) {
    return viewCache.getView(myFlow, url);
}

function getViewModel(myFlow: MyFlow, viewModelId: string) {
    return viewModelFactory.getViewModel(myFlow, viewModelId);
}

function saveBlacklist(myFlow: MyFlow, blacklist: string[]) {
    return myFlow.postJSON(config.partialUrls.saveBlacklist, JSON.stringify(blacklist));
}

function getProductConfigManifest(myFlow: MyFlow, productBaseUrl: string, productId: string) {
    return myFlow.getJSONWithAnotherBaseUrl<ProductConfigManifest>(
        productBaseUrl,
        config.partialUrls.getProductConfigManifest,
        {
            id: productId,
            //ecma: environment.getEcmaVersion(),
            uiCulture: environment.getUiCulture()
        },
        true
    );
}

function getProductConfigManifestFlow3(myFlow: MyFlow, productBaseUrl: string, productId: string) {
    return myFlow.getJSONWithAnotherBaseUrl<ProductConfigManifest>(
        productBaseUrl,
        config.partialUrls.getProductConfigManifest,
        {
            id: productId,
            ecma: 6,
            uiCulture: environment.getUiCulture()
        },
        true
    );
}

function validateAndSanitize(myFlow: MyFlow, productBaseUrl: string, productId: string, payload: Dictionary) {
    if (payload) {
        return myFlow.getJSONWithAnotherBaseUrl<any>(
            productBaseUrl,
            config.partialUrls.validateAndSanitize,
            {
                id: productId,
                payload: JSON.stringify(payload),
                uiCulture: environment.getUiCulture()
            },
            true
        );
    }
    return myFlow.getJSONWithAnotherBaseUrl<any>(
        productBaseUrl,
        config.partialUrls.validateAndSanitize,
        {
            id: productId,
            uiCulture: environment.getUiCulture()
        },
        true
    );
}

function saveSelectedProduct(myFlow: MyFlow, selectedProduct: SelectedProduct) {
    return myFlow.postJSON(config.partialUrls.saveSelectedProduct, JSON.stringify(selectedProduct));
}

function clearSelectedProduct(myFlow: MyFlow) {
    return myFlow.deleteJSON<void>(config.partialUrls.clearSelectedProduct, undefined, true);
}

function hasSelectedProduct(myFlow: MyFlow) {
    return myFlow.getJSON(config.partialUrls.hasSelectedProduct);
}

function selectTheme(myFlow: MyFlow, themeId: string) {
    return myFlow.postJSON<void>(config.partialUrls.selectTheme, JSON.stringify({ id: themeId }));
}

function enterSingleModuleMode(myFlow: MyFlow, id: string) {
    return myFlow.postJSON<void>(config.partialUrls.enterSingleModuleMode, JSON.stringify({ id }));
}

function exitSingleModuleMode(myFlow: MyFlow) {
    return myFlow.postJSON<void>(config.partialUrls.exitSingleModuleMode, {});
}

function getHomeConfig(myFlow: MyFlow, productId: string) {
    return myFlow.getJSON<HomeConfig>(config.partialUrls.homeConfigGet, { productId });
}

function updateHomeConfig(myFlow: MyFlow, productId: string, homeConfig: HomeConfig) {
    return myFlow.postJSON(
        config.partialUrls.homeConfigUpdate,
        JSON.stringify({
            productId,
            config: homeConfig
        })
    );
}

function resetHomeConfig(myFlow: MyFlow, productId: string) {
    return myFlow.deleteJSON(config.partialUrls.homeConfigReset + "?productId=" + encodeURIComponent(productId), null);
}

function whoami(myFlow: MyFlow) {
    return myFlow.get<string>(config.partialUrls.whoami);
}

export default {
    getWorkspace,
    getWebModule,
    loadBundle,
    getView,
    getViewModel,
    saveBlacklist,
    getProductConfigManifest,
    getProductConfigManifestFlow3,
    validateAndSanitize,
    saveSelectedProduct,
    clearSelectedProduct,
    hasSelectedProduct,
    selectTheme,
    enterSingleModuleMode,
    exitSingleModuleMode,
    getHomeConfig,
    resetHomeConfig,
    updateHomeConfig,
    whoami
};
